import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AppProvider, Frame, Navigation, TopBar, Spinner } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';
import {
  HomeIcon,
  OrderIcon,
  ProductIcon,
  PersonIcon,
  DeliveryIcon,
  ReceiptIcon,
  ChartVerticalIcon,
  CashDollarIcon,
} from '@shopify/polaris-icons';
import { useState, useCallback, lazy, Suspense } from 'react';
import { useSessionBootstrap } from './hooks/useSessionBootstrap';

// Pages
// Dashboard is route-split (lazy) so its heavy chart dependencies (recharts)
// stay out of the entry bundle.
const Dashboard = lazy(() => import('./pages/Dashboard'));
import WarehouseQueue from './pages/warehouse/WarehouseQueue';
import WarehouseDispatch from './pages/warehouse/WarehouseDispatch';
import MiscShipments from './pages/warehouse/MiscShipments';
import ReturnsPending from './pages/returns/ReturnsPending';
import ReturnsQc from './pages/returns/ReturnsQc';
import ReturnsRepair from './pages/returns/ReturnsRepair';
import AssetsList from './pages/assets/AssetsList';
import AssetDetail from './pages/assets/AssetDetail';
import SerialSearch from './pages/assets/SerialSearch';
import ReceiveRentalProduct from './pages/assets/ReceiveRentalProduct';
import AddProductInventory from './pages/assets/AddProductInventory';
import ClinicsList from './pages/clinics/ClinicsList';
import ClinicDetail from './pages/clinics/ClinicDetail';
import AdminCreateClinicOrder from './pages/clinics/AdminCreateClinicOrder';
import RentalsList from './pages/rentals/RentalsList';
import RentalDetail from './pages/rentals/RentalDetail';
import QuickOrder from './pages/rentals/QuickOrder';
import SalesAgentsList from './pages/sales/SalesAgentsList';
import SalesAgentDetail from './pages/sales/SalesAgentDetail';
import PatientsList from './pages/patients/PatientsList';
import PatientDetail from './pages/patients/PatientDetail';
import DoctorsList from './pages/doctors/DoctorsList';
import DoctorDetail from './pages/doctors/DoctorDetail';
import TodaysSendOrders from './pages/rentals/TodaysSendOrders';
import ReportsIndex from './pages/reports/ReportsIndex';
import SalesSummary from './pages/reports/SalesSummary';
import ClinicRevenue from './pages/reports/ClinicRevenue';
import AgentCommission from './pages/reports/AgentCommission';
import AgedReceivables from './pages/reports/AgedReceivables';
import InventoryUsage from './pages/reports/InventoryUsage';
import ConsignmentReport from './pages/reports/ConsignmentReport';
import OverdueReport from './pages/reports/OverdueReport';
import FulfilledByClinic from './pages/reports/FulfilledByClinic';
import PatientReport from './pages/reports/PatientReport';
import SerialReport from './pages/reports/SerialReport';
import ProductReport from './pages/reports/ProductReport';
import AllOrdersBilling from './pages/reports/AllOrdersBilling';
import MissingSendDates from './pages/reports/MissingSendDates';
import SentOrders from './pages/reports/SentOrders';
import DailyManifest from './pages/shipping/DailyManifest';
import EstimatesList from './pages/estimates/EstimatesList';
import EstimateCreate from './pages/estimates/EstimateCreate';
import EstimateDetail from './pages/estimates/EstimateDetail';
import InvoicesList from './pages/billing/InvoicesList';

import ReceiveProduct from './pages/returns/ReceiveProduct';
import ProductsList from './pages/products/ProductsList';
import ConsignmentInventory from './pages/consignment/ConsignmentInventory';

// Clinic portal pages
import ClinicLayout from './pages/clinic/ClinicLayout';
import ClinicLogin from './pages/clinic/ClinicLogin';
import ClinicDashboard from './pages/clinic/ClinicDashboard';
import ClinicOrders from './pages/clinic/ClinicOrders';
import ClinicPatients from './pages/clinic/ClinicPatients';
import ClinicDoctors from './pages/clinic/ClinicDoctors';
import ClinicReports from './pages/clinic/ClinicReports';
import ClinicReturnRequest from './pages/clinic/ClinicReturnRequest';
import ClinicOrderDetail from './pages/clinic/ClinicOrderDetail';
import ClinicAddPatient from './pages/clinic/ClinicAddPatient';
import ClinicNotReturned from './pages/clinic/ClinicNotReturned';
import ClinicOverdue from './pages/clinic/ClinicOverdue';
import ClinicTomorrowOverdue from './pages/clinic/ClinicTomorrowOverdue';
import ClinicProfile from './pages/clinic/ClinicProfile';
import ClinicCreateOrderProducts from './pages/clinic/ClinicCreateOrderProducts';
import ClinicCreateOrderDetail from './pages/clinic/ClinicCreateOrderDetail';
import ClinicCheckout from './pages/clinic/ClinicCheckout';
import ClinicPatientDashboard from './pages/clinic/ClinicPatientDashboard';
import { ClinicAuthProvider } from './contexts/ClinicAuthContext';

function RouteLoadingFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '5rem 0' }}>
      <Spinner accessibilityLabel="Loading page" size="large" />
    </div>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);

  // Bootstrap: exchange session token for offline access token on first load
  useSessionBootstrap();

  const toggleMobileNavigation = useCallback(() => {
    setMobileNavigationActive((active) => !active);
  }, []);

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={[
          {
            label: 'Dashboard',
            icon: HomeIcon,
            onClick: () => navigate('/'),
            selected: location.pathname === '/',
          },
        ]}
      />
      <Navigation.Section
        title="Operations"
        items={[
          {
            // "Dispatch", not "Warehouse". The team says dispatch, the sub-items
            // already said dispatch, and only the parent said warehouse — which left
            // them asking whether the dispatch tab and the warehouse tab were the
            // same screen.
            label: 'Dispatch',
            icon: DeliveryIcon,
            onClick: () => navigate('/dispatch/today'),
            selected:
              location.pathname.startsWith('/warehouse') ||
              location.pathname.startsWith('/dispatch'),
            subNavigationItems: [
              {
                // The answer to "a tab that only shows what has to go out" — so it
                // leads, and its name says what it contains.
                label: 'Going Out Today',
                url: '/dispatch/today',
              },
              {
                label: 'Assign Serials',
                url: '/warehouse',
              },
              {
                label: 'Dispatch an Order',
                url: '/warehouse/dispatch',
              },
              {
                label: 'Misc. Shipments',
                url: '/warehouse/misc-shipments',
              },
              {
                label: 'Daily Manifest',
                url: '/shipping/manifest',
              },
            ],
          },
          {
            label: 'Returns',
            icon: ReceiptIcon,
            onClick: () => navigate('/returns'),
            selected: location.pathname.startsWith('/returns'),
            subNavigationItems: [
              {
                label: 'Pending Returns',
                url: '/returns',
              },
              {
                label: 'QC Queue',
                url: '/returns/qc',
              },
              {
                label: 'Repair Queue',
                url: '/returns/repair',
              },
              {
                label: 'Receive Product',
                url: '/returns/receive',
              },
            ],
          },
          {
            label: 'Consignment Stock',
            icon: ProductIcon,
            onClick: () => navigate('/consignment'),
            selected: location.pathname === '/consignment',
          },
        ]}
      />
      <Navigation.Section
        title="Management"
        items={[
          {
            label: 'Rentals',
            icon: OrderIcon,
            onClick: () => navigate('/rentals'),
            selected: location.pathname.startsWith('/rentals'),
          },
          {
            label: 'Products',
            icon: ProductIcon,
            onClick: () => navigate('/products'),
            selected: location.pathname.startsWith('/products'),
          },
          {
            label: 'Assets',
            icon: ProductIcon,
            onClick: () => navigate('/assets'),
            selected: location.pathname.startsWith('/assets'),
            subNavigationItems: [
              {
                label: 'Track Serial Number',
                url: '/assets/search',
              },
              {
                label: 'Receive Rental Product',
                url: '/assets/receive',
              },
              {
                label: 'Add Product Inventory',
                url: '/assets/add',
              },
            ],
          },
          {
            label: 'Clinics',
            icon: PersonIcon,
            onClick: () => navigate('/clinics'),
            selected: location.pathname.startsWith('/clinics'),
          },
          {
            label: 'Patients',
            icon: PersonIcon,
            onClick: () => navigate('/patients'),
            selected: location.pathname.startsWith('/patients'),
          },
          {
            label: 'Doctors',
            icon: PersonIcon,
            onClick: () => navigate('/doctors'),
            selected: location.pathname.startsWith('/doctors'),
          },
          {
            label: 'Estimates',
            icon: OrderIcon,
            onClick: () => navigate('/estimates'),
            selected: location.pathname.startsWith('/estimates'),
          },
          {
            label: 'Invoices',
            icon: ReceiptIcon,
            onClick: () => navigate('/billing/invoices'),
            selected: location.pathname.startsWith('/billing'),
          },
          {
            label: 'Sales Agents',
            icon: CashDollarIcon,
            onClick: () => navigate('/sales'),
            selected: location.pathname.startsWith('/sales'),
          },
        ]}
      />
      <Navigation.Section
        title="Reports"
        items={[
          {
            label: 'Analytics',
            icon: ChartVerticalIcon,
            onClick: () => navigate('/reports'),
            selected: location.pathname.startsWith('/reports'),
            subNavigationItems: [
              {
                label: 'Sales Summary',
                url: '/reports/sales-summary',
              },
              {
                label: 'Clinic Revenue',
                url: '/reports/clinic-revenue',
              },
              {
                label: 'Agent Commission',
                url: '/reports/agent-commission',
              },
              {
                label: 'Aged Receivables',
                url: '/reports/aged-receivables',
              },
              {
                label: 'Inventory Usage',
                url: '/reports/inventory-usage',
              },
              {
                label: 'Consignment',
                url: '/reports/consignment',
              },
              {
                label: 'Overdue Rentals',
                url: '/reports/overdue',
              },
              {
                label: 'Fulfilled by Clinic',
                url: '/reports/fulfilled-by-clinic',
              },
              {
                label: 'Patient',
                url: '/reports/patient',
              },
              {
                label: 'Serial Number',
                url: '/reports/serial',
              },
              {
                label: 'Product',
                url: '/reports/product',
              },
            ],
          },
        ]}
      />
    </Navigation>
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      onNavigationToggle={toggleMobileNavigation}
    />
  );

  // Clinic portal uses its own layout — render it outside the admin Frame
  if (location.pathname === '/clinic' || location.pathname.startsWith('/clinic/')) {
    return (
      <AppProvider i18n={enTranslations}>
        <ClinicAuthProvider>
          <Routes>
            <Route path="/clinic/login" element={<ClinicLogin />} />
            <Route path="/clinic" element={<ClinicLayout />}>
              <Route index element={<ClinicDashboard />} />
              <Route path="orders" element={<ClinicOrders />} />
              <Route path="orders/:id" element={<ClinicOrderDetail />} />
              <Route path="patients" element={<ClinicPatients />} />
              <Route path="patients/new" element={<ClinicAddPatient />} />
              <Route path="patients/:id" element={<ClinicPatientDashboard />} />
              <Route path="orders/new" element={<ClinicCreateOrderProducts />} />
              <Route path="orders/new/configure" element={<ClinicCreateOrderDetail />} />
              <Route path="orders/new/checkout" element={<ClinicCheckout />} />
              <Route path="doctors" element={<ClinicDoctors />} />
              <Route path="reports" element={<ClinicReports />} />
              <Route path="reports/not-returned" element={<ClinicNotReturned />} />
              <Route path="reports/overdue" element={<ClinicOverdue />} />
              <Route path="reports/tomorrow-overdue" element={<ClinicTomorrowOverdue />} />
              <Route path="return-request" element={<ClinicReturnRequest />} />
              <Route path="profile" element={<ClinicProfile />} />
            </Route>
          </Routes>
        </ClinicAuthProvider>
      </AppProvider>
    );
  }

  return (
    <AppProvider i18n={enTranslations}>
      <Frame
        topBar={topBarMarkup}
        navigation={navigationMarkup}
        showMobileNavigation={mobileNavigationActive}
        onNavigationDismiss={toggleMobileNavigation}
      >
        <Routes>
          <Route
            path="/"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <Dashboard />
              </Suspense>
            }
          />

          {/* Warehouse Routes */}
          <Route path="/warehouse" element={<WarehouseQueue />} />
          <Route path="/warehouse/dispatch" element={<WarehouseDispatch />} />
          <Route path="/warehouse/dispatch/:id" element={<WarehouseDispatch />} />
          <Route path="/warehouse/misc-shipments" element={<MiscShipments />} />
          <Route path="/dispatch/today"element={<TodaysSendOrders />} />
          <Route path="/shipping/manifest" element={<DailyManifest />} />

          {/* Consignment Routes */}
          <Route path="/consignment" element={<ConsignmentInventory />} />

          {/* Returns Routes */}
          <Route path="/returns" element={<ReturnsPending />} />
          <Route path="/returns/qc" element={<ReturnsQc />} />
          <Route path="/returns/repair" element={<ReturnsRepair />} />
          <Route path="/returns/receive" element={<ReceiveProduct />} />

          {/* Rentals Routes */}
          <Route path="/rentals" element={<RentalsList />} />
          <Route path="/rentals/new" element={<QuickOrder />} />
          <Route path="/rentals/:id" element={<RentalDetail />} />

          {/* Products Routes */}
          <Route path="/products" element={<ProductsList />} />

          {/* Assets Routes */}
          <Route path="/assets" element={<AssetsList />} />
          <Route path="/assets/search" element={<SerialSearch />} />
          <Route path="/assets/receive" element={<ReceiveRentalProduct />} />
          <Route path="/assets/add" element={<AddProductInventory />} />
          <Route path="/assets/:id" element={<AssetDetail />} />

          {/* Clinics Routes */}
          <Route path="/clinics" element={<ClinicsList />} />
          <Route path="/clinics/:id" element={<ClinicDetail />} />
          <Route path="/clinics/:id/create-order" element={<AdminCreateClinicOrder />} />

          {/* Patients Routes */}
          <Route path="/patients" element={<PatientsList />} />
          <Route path="/patients/:id" element={<PatientDetail />} />

          {/* Doctors Routes */}
          <Route path="/doctors" element={<DoctorsList />} />
          <Route path="/doctors/:id" element={<DoctorDetail />} />

          {/* Sales Agents Routes */}
          <Route path="/sales" element={<SalesAgentsList />} />
          <Route path="/sales/:id" element={<SalesAgentDetail />} />

          {/* Estimates Routes */}
          <Route path="/estimates" element={<EstimatesList />} />
          <Route path="/estimates/new" element={<EstimateCreate />} />
          <Route path="/estimates/:id" element={<EstimateDetail />} />

          {/* Billing Routes */}
          <Route path="/billing/invoices" element={<InvoicesList />} />

          {/* Reports Routes */}
          <Route path="/reports" element={<ReportsIndex />} />
          <Route path="/reports/sales-summary" element={<SalesSummary />} />
          <Route path="/reports/clinic-revenue" element={<ClinicRevenue />} />
          <Route path="/reports/agent-commission" element={<AgentCommission />} />
          <Route path="/reports/aged-receivables" element={<AgedReceivables />} />
          <Route path="/reports/inventory-usage" element={<InventoryUsage />} />
          <Route path="/reports/consignment" element={<ConsignmentReport />} />
          <Route path="/reports/overdue" element={<OverdueReport />} />
          <Route path="/reports/fulfilled-by-clinic" element={<FulfilledByClinic />} />
          <Route path="/reports/patient" element={<PatientReport />} />
          <Route path="/reports/serial" element={<SerialReport />} />
          <Route path="/reports/product" element={<ProductReport />} />
          <Route path="/reports/billing" element={<AllOrdersBilling />} />
          <Route path="/reports/missing-send-dates" element={<MissingSendDates />} />
          <Route path="/reports/sent-orders" element={<SentOrders />} />
        </Routes>
      </Frame>
    </AppProvider>
  );
}

export default App;
